import { InversionContainer } from '@/controller/container'
import { InjectionTokens } from '@/controller/tokens'

import { UseCase } from '@/domain/core/UseCase'
import { Result } from '@/domain/protocols/Result'

import { hasUserChartTiles, registerUserChartTiles } from '@/data/UserChartRepository/userChartTileStore'
import { extractTilesFromZip } from '@/data/UserChartRepository/userChartTilesZip'

import type { IChartConverterWebClient } from '@/infra/http/ChartConverterWebClient/IChartConverterWebClient'
import type { IUserChartsWebClient } from '@/infra/http/UserChartsWebClient/IUserChartsWebClient'

// Bounded retry for the tiles download. Only transient transport failures
// (NETWORK_ERROR) are retried; a definitive server response (DOWNLOAD_FAILED, e.g.
// the chart has no processed tiles yet / was removed) is permanent and returned
// immediately so callers can act on it (drop the overlay) instead of spinning.
export const DOWNLOAD_MAX_ATTEMPTS = 3
export const DOWNLOAD_RETRY_BASE_MS = 1000

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

export interface EnsureUserChartTilesOptions {
  // When the processed tiles are missing on the server (permanent DOWNLOAD_FAILED),
  // try to re-hydrate by downloading the original and re-converting it. Enabled by
  // interactive callers (edition preview, toolbar, "show on map"); the boot prefetch
  // leaves it off so it does not trigger expensive conversions in bulk.
  recoverFromOriginal?: boolean
  // The chart's imported file name, used to name the re-converted original so the
  // converter picks the right processing path (e.g. .mbtiles vs .pdf).
  originalFileName?: string
}

// Makes sure a chart's tiles are available in the in-memory store before it is
// shown on the map. Resolution order:
//   1. In-memory store (tiles registered earlier this session) — nothing to do.
//   2. Server — download the processed tiles archive and register it (with a bounded
//      retry for transient network failures, so a flaky connection self-heals).
//   3. Recovery (opt-in) — if the processed tiles are gone but the original is still
//      on the server, download and re-convert it (which re-registers the tiles and
//      re-queues the processed upload, so the chart is fixed on the server too).
// Tiles are never persisted: they are kept only in the in-memory store and
// re-downloaded each session (prefetched at boot by UserChartTilesPrefetchService),
// so the feature does not depend on per-browser local storage.
export class EnsureUserChartTilesUseCase implements UseCase<string, Promise<Result<void>>> {
  // Resolved lazily so the container is only touched when a dependency was not injected
  // AND is actually used (keeps unit tests that pass a stub web client container-free;
  // the converter is only needed on the opt-in recovery path).
  private webClientOverride?: IUserChartsWebClient
  private converterOverride?: IChartConverterWebClient

  constructor(webClient?: IUserChartsWebClient, converter?: IChartConverterWebClient) {
    this.webClientOverride = webClient
    this.converterOverride = converter
  }

  private get webClient(): IUserChartsWebClient {
    return (
      this.webClientOverride ??
      InversionContainer.getInstance().getContainer().get<IUserChartsWebClient>(InjectionTokens.UserChartsWebClient)
    )
  }

  private get converter(): IChartConverterWebClient {
    return (
      this.converterOverride ??
      InversionContainer.getInstance()
        .getContainer()
        .get<IChartConverterWebClient>(InjectionTokens.ChartConverterWebClient)
    )
  }

  async execute(chartId: string, options: EnsureUserChartTilesOptions = {}): Promise<Result<void>> {
    // 1. Already in the in-memory store (registered earlier this session).
    if (hasUserChartTiles(chartId)) return Result.ok()

    // 2. Download the processed tiles from the server and register them.
    const download = await this.downloadWithRetry(chartId)
    if (download.isSuccess) {
      return this.registerArchive(chartId, download.getValue())
    }

    // 3. The processed tiles are gone. If allowed, try to re-hydrate from the original
    //    still on the server by re-converting it.
    if (download.error === 'DOWNLOAD_FAILED' && options.recoverFromOriginal) {
      const recovered = await this.recoverFromOriginal(chartId, options.originalFileName)
      if (recovered.isSuccess) return recovered
      // Fall through to the original failure so callers keep treating it as "missing".
    }

    return Result.fail(download.error)
  }

  // Re-hydrates a chart by downloading its original from the server and re-converting
  // it. The converter re-registers the tiles in memory and re-queues the processed
  // upload (so the next sync fixes the chart on the server), while skipping the
  // original re-upload since it is already there.
  private async recoverFromOriginal(chartId: string, originalFileName?: string): Promise<Result<void>> {
    const original = await this.webClient.downloadOriginal(chartId)
    if (original.isFailure) return Result.fail(original.error)

    const name = originalFileName || `${chartId}.pdf`
    const file = new File([original.getValue()], name)

    const converted = await this.converter.convert({ file, chartId, skipOriginalPersistence: true })
    if (converted.isFailure) return Result.fail(converted.error)

    // The converter already registered the tiles in the in-memory store.
    return hasUserChartTiles(chartId) ? Result.ok() : Result.fail('NO_TILES')
  }

  private async registerArchive(chartId: string, archive: ArrayBuffer): Promise<Result<void>> {
    try {
      const { tiles } = await extractTilesFromZip(archive)
      if (tiles.size === 0) return Result.fail('NO_TILES')
      registerUserChartTiles(chartId, tiles)
      return Result.ok()
    } catch {
      return Result.fail('TILES_EXTRACTION_FAILED')
    }
  }

  private async downloadWithRetry(chartId: string): Promise<Result<ArrayBuffer>> {
    let last: Result<ArrayBuffer> = Result.fail('NETWORK_ERROR')
    for (let attempt = 0; attempt < DOWNLOAD_MAX_ATTEMPTS; attempt++) {
      last = await this.webClient.downloadProcessed(chartId)
      if (last.isSuccess) return last

      // Permanent (server) failure: no point retrying.
      if (last.error !== 'NETWORK_ERROR') return last

      if (attempt < DOWNLOAD_MAX_ATTEMPTS - 1) {
        await delay(DOWNLOAD_RETRY_BASE_MS * 2 ** attempt)
      }
    }
    return last
  }
}
